import { formatDurationSeconds } from './chartFormatUtils';

const NON_DISPLAY_COLUMNS = new Set([
  'sessions',
  'reply_details',
  'session_id',
  'reply_count',
  'pending_reply_count',
  'avg_seconds',
  'first_reply_seconds',
  'last_activity_at',
  'started_at',
  'ended_at',
  'session_end_at',
  'customer_last_reply_at',
  'avg_display',
  'first_reply_display',
  'customers',
  'customers_truncated',
  '_table_meta',
]);

const VALUE_COLUMNS = new Set(['value', 'count', 'total']);

const KNOWN_COLUMN_LABELS = {
  avg_first_reply: 'Avg first reply',
  average_first_reply: 'Average first reply',
  chats: 'Sessions with activity',
  Chats: 'Sessions with activity',
  response_agent_username: 'Agent Name',
  'response agent username': 'Agent Name',
  'response_agent_username': 'Agent Name',
  agent_name: 'Agent Name',
  'agent name': 'Agent Name',
  avg_agent_response: 'Avg agent response',
  agent_replies: 'Agent replies',
  messages: 'Messages',
  inbound: 'Inbound',
  outbound_human: 'Outbound (human)',
  avg_customer_response: 'Avg customer response',
  'sessions started': 'Sessions started',
  'session started': 'Sessions started',
  sessions_started: 'Sessions started',
  session_started: 'Sessions started',
  number_of_sessions: 'Sessions started',
  'number of sessions': 'Sessions started',
  'Number of sessions': 'Sessions started',
};

export function isValueColumn(column) {
  return VALUE_COLUMNS.has(String(column || '').toLowerCase());
}

export function columnLabel(column, { categoryLabel, measureLabel, columnLabels } = {}) {
  const str = String(column || '').trim();
  const normalizedKey = str.toLowerCase();
  const underscoredKey = normalizedKey.replace(/[\s-]+/g, '_');

  if (KNOWN_COLUMN_LABELS[normalizedKey]) return KNOWN_COLUMN_LABELS[normalizedKey];
  if (KNOWN_COLUMN_LABELS[underscoredKey]) return KNOWN_COLUMN_LABELS[underscoredKey];
  if (columnLabels?.[column]) return columnLabels[column];
  if (columnLabels?.[underscoredKey]) return columnLabels[underscoredKey];
  if (isValueColumn(column)) return measureLabel || 'Value';
  if (column === 'group' || column === 'label' || column === 'date') {
    return categoryLabel || 'Category';
  }
  return column;
}

function seriesKey(series, index) {
  return series?.id || series?.label || `value_${index}`;
}

function enrichInterveneBenchmarkRow(row) {
  if (!row || !Array.isArray(row.sessions) || !row.sessions.length) return row;

  let replyCount = 0;
  let responseSecondsTotal = 0;
  let responseSecondsCount = 0;
  const firstReplySeconds = [];

  row.sessions.forEach((session) => {
    const replyDetails = Array.isArray(session?.reply_details)
      ? session.reply_details
      : [];
    const sessionReplyCount = Number(
      session?.reply_count ?? session?.Replies ?? replyDetails.length
    );
    replyCount += Number.isFinite(sessionReplyCount) ? sessionReplyCount : replyDetails.length;

    const validSeconds = replyDetails
      .map((reply) => Number(reply?.response_seconds))
      .filter((seconds) => Number.isFinite(seconds));

    if (validSeconds.length) {
      responseSecondsTotal += validSeconds.reduce((total, seconds) => total + seconds, 0);
      responseSecondsCount += validSeconds.length;
    } else if (Number.isFinite(Number(session?.avg_seconds)) && sessionReplyCount > 0) {
      responseSecondsTotal += Number(session.avg_seconds) * sessionReplyCount;
      responseSecondsCount += sessionReplyCount;
    }

    const firstSeconds = Number(
      session?.first_reply_seconds ?? replyDetails[0]?.response_seconds
    );
    if (Number.isFinite(firstSeconds)) firstReplySeconds.push(firstSeconds);
  });

  if (replyCount <= 0) return row;

  const enriched = { ...row, 'Agent replies': replyCount };
  if (responseSecondsCount > 0) {
    enriched['Avg agent response'] = formatDurationSeconds(
      responseSecondsTotal / responseSecondsCount
    );
  }
  if (firstReplySeconds.length) {
    const firstReplyAverage = firstReplySeconds.reduce((total, seconds) => total + seconds, 0)
      / firstReplySeconds.length;
    enriched['Avg first reply'] = formatDurationSeconds(firstReplyAverage);
  }
  return enriched;
}

export function reportTableModel(result) {
  const rawInitialColumns = Array.isArray(result?.columns) && result.columns.length
    ? [...result.columns]
    : (Array.isArray(result?.rows) && result.rows.length && typeof result.rows[0] === 'object'
      ? Object.keys(result.rows[0])
      : null);

  const rawColumns = rawInitialColumns
    ? rawInitialColumns.filter((c) => !NON_DISPLAY_COLUMNS.has(String(c || '').trim().toLowerCase()))
    : null;

  if (Array.isArray(result?.rows) && rawColumns && rawColumns.length) {
    const isAgentBenchmark = rawColumns.some((c) => /response\s*agent\s*username/i.test(c))
      && rawColumns.some((c) => /agent\s*response/i.test(c));

    if (isAgentBenchmark) {
      let columns = [...rawColumns];

      // Normalize session-started column key (display label via columnLabel)
      const sessionColIdx = columns.findIndex((c) => /sessions?\s*started|number\s*of\s*sessions/i.test(c));
      if (sessionColIdx !== -1) {
        columns[sessionColIdx] = 'Sessions started';
      }

      // Filter out any internal columns and duplicate columns
      columns = columns.filter((c, idx) => {
        if (NON_DISPLAY_COLUMNS.has(String(c || '').trim().toLowerCase())) return false;
        return columns.indexOf(c) === idx;
      });

      const hasFirstReply = columns.some((c) => /first\s*reply/i.test(c));
      const hasChats = columns.some((c) => /^chats$/i.test(c));
      const hasSessions = columns.some((c) => /session/i.test(c));

      if (!hasFirstReply) {
        const respIdx = columns.findIndex((c) => /agent\s*response/i.test(c));
        columns.splice(respIdx !== -1 ? respIdx + 1 : columns.length, 0, 'Avg first reply');
      }

      if (!hasChats) {
        const replyIdx = columns.findIndex((c) => /first\s*reply/i.test(c));
        columns.splice(replyIdx !== -1 ? replyIdx + 1 : columns.length, 0, 'Chats');
      }

      if (!hasSessions) {
        const chatsIdx = columns.findIndex((c) => /^chats$/i.test(c));
        columns.splice(chatsIdx !== -1 ? chatsIdx + 1 : columns.length, 0, 'Sessions started');
      }

      const rows = result.rows.map((row) => {
        if (!row || typeof row !== 'object') return row;
        const copy = { ...row };
        if (copy['Avg first reply'] === undefined) {
          copy['Avg first reply'] = copy['Average first reply']
            ?? copy['avg_first_reply']
            ?? copy['avg_first_agent_response']
            ?? copy['First reply']
            ?? '—';
        }
        if (copy['Chats'] === undefined) {
          copy['Chats'] = copy['chats']
            ?? copy['session_count']
            ?? copy['chat_count']
            ?? '—';
        }
        const sessionsStartedValue = copy['Sessions started']
          ?? copy['Number of sessions']
          ?? copy['session started']
          ?? copy['sessions_started']
          ?? copy['session_started']
          ?? copy['number_of_sessions']
          ?? (Array.isArray(copy['sessions']) ? copy['sessions'].length : undefined)
          ?? '—';
        copy['Sessions started'] = sessionsStartedValue;
        copy['Number of sessions'] = sessionsStartedValue;
        return enrichInterveneBenchmarkRow(copy);
      });

      return { columns, rows };
    }

    return {
      columns: rawColumns,
      rows: result.rows,
    };
  }

  if (result?.categories && Array.isArray(result?.series) && result.series.length) {
    const columns = ['label', ...result.series.map(seriesKey)];
    const rows = result.categories.map((label, rowIndex) => {
      const row = { label: label ?? '' };
      result.series.forEach((series, seriesIndex) => {
        row[seriesKey(series, seriesIndex)] = series?.data?.[rowIndex] ?? 0;
      });
      return row;
    });
    return { columns, rows };
  }

  const points = result?.series?.[0]?.data;
  if (Array.isArray(points) && points.length && (points[0]?.x !== undefined || points[0]?.y !== undefined)) {
    return {
      columns: ['label', 'value'],
      rows: points.map((point) => ({
        label: point?.x ?? '',
        value: point?.y ?? 0,
      })),
    };
  }

  if (Array.isArray(result?.metrics) && result.metrics.length) {
    return {
      columns: ['Metric', 'Value'],
      rows: result.metrics.map((item) => ({
        Metric: item.label || item.id,
        Value: item.display != null ? item.display : (item.value != null ? item.value : 0),
      })),
    };
  }

  if (result?.kind === 'number' && (result?.value != null || result?.display != null)) {
    return {
      columns: ['Metric', 'Value'],
      rows: [
        {
          Metric: result.label || 'Value',
          Value: result.display != null ? result.display : result.value,
        },
      ],
    };
  }

  return { columns: [], rows: [] };
}

export function isBlankCell(value) {
  if (value == null || value === '') return true;
  if (typeof value === 'object') return !(value.label || value.name);
  return false;
}

export function hasBlankBreakdown(columns, rows) {
  const categoryColumns = (columns || []).filter((column) => !isValueColumn(column));
  return (rows || []).some((row) => (
    categoryColumns.some((column) => isBlankCell(row?.[column]))
  ));
}

export function compareTableValues(left, right) {
  if (isBlankCell(left) && isBlankCell(right)) return 0;
  if (isBlankCell(left)) return 1;
  if (isBlankCell(right)) return -1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), undefined, { numeric: true, sensitivity: 'base' });
}

export function sortTableRows(rows, column, direction) {
  const factor = direction === 'desc' ? -1 : 1;
  return [...(rows || [])].sort(
    (left, right) => factor * compareTableValues(left?.[column], right?.[column]),
  );
}

export function sumColumn(rows, column) {
  return (rows || []).reduce((total, row) => {
    const value = row?.[column];
    return typeof value === 'number' && Number.isFinite(value) ? total + value : total;
  }, 0);
}

export function isTotalColumn(column, index, rows) {
  if (index === 0 && !isValueColumn(column)) return false;
  return (rows || []).some((row) => (
    typeof row?.[column] === 'number' && Number.isFinite(row[column])
  ));
}

export function formatTableCell(value, isValue, valueFormat) {
  if (value === null || value === undefined || value === '') {
    return isValue ? '—' : 'Unknown';
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    if (isValue && valueFormat === 'duration') {
      return formatDurationSeconds(value);
    }
    return value;
  }
  if (Array.isArray(value)) {
    const parts = value
      .map((item) => formatTableCell(item, isValue, valueFormat))
      .filter((item) => item && item !== '—' && item !== 'Unknown');
    return parts.join(', ') || (isValue ? '—' : 'Unknown');
  }
  if (typeof value === 'object') {
    if (value.label || value.name) return value.label || value.name;
    if (value.value != null && typeof value.value !== 'object') {
      if (isValue && valueFormat === 'duration' && typeof value.value === 'number') {
        return formatDurationSeconds(value.value);
      }
      return String(value.value);
    }
    return isValue ? '—' : 'Unknown';
  }
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text;
  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) return text;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
